import { z } from 'zod'

import {
  SORT_ONLY_KEYS,
  knownRanges,
  parseFilters,
  parseSort,
} from '@/features/screener/lib/filters'
import { codeParamSchema, searchTextParamSchema } from '@/schemas/common'
import {
  metricKeySchema,
  type Metric,
  type ScreenFilter,
  type ScreenQuery,
} from '@/schemas/screener'

/**
 * Everything that makes a screen what it is: the filters, the sector, the order, the page, the
 * page size, the column set and the view. One plain object, so whoever hosts the screener keeps it
 * where it likes: `/screener/advanced` in its search params (a link reopens the same screen), the
 * chart workspace's tab in its own store.
 *
 * Every field falls back instead of throwing (`.catch`): a hand-edited or stale value lands on the
 * default screen, never an error page. No `createFileRoute` here, so a test may import it.
 */

export const SCREENER_VIEWS = ['table', 'chart', 'heatmap'] as const

export type ScreenerView = (typeof SCREENER_VIEWS)[number]

/** "Rows per page", the mock's options (`ype`). mm-be takes up to 200. */
export const SCREENER_PAGE_SIZES = [10, 20, 50] as const

export type ScreenerPageSize = (typeof SCREENER_PAGE_SIZES)[number]

export const screenStateSchema = z.object({
  /** The ranges, in `parseFilters`' format. */
  f: searchTextParamSchema.optional().catch(undefined),
  /** A `/market/sectors/` key. */
  sector: codeParamSchema.optional().catch(undefined),
  /** A sort key, `-` first for descending. Absent is mm-be's order, by symbol. */
  sort: z
    .string()
    .regex(/^-?[a-z][a-z0-9_]*$/)
    .optional()
    .catch(undefined),
  page: z.number().int().min(1).default(1).catch(1),
  pageSize: z.literal(SCREENER_PAGE_SIZES).default(10).catch(10),
  /** The column set: a metric group key. Absent, or one `/metrics/` no longer has, is the first. */
  set: metricKeySchema.optional().catch(undefined),
  view: z.enum(SCREENER_VIEWS).default('table').catch('table'),
})

export type ScreenState = z.infer<typeof screenStateSchema>

/** A fresh, unfiltered screen: the table, Overview, mm-be's order, page 1 of 10. */
export const DEFAULT_SCREEN_STATE: ScreenState = screenStateSchema.parse({})

/**
 * Anything (a URL's search, a persisted store) as a screen state. Keys it does not know are
 * dropped, so two hosts holding the same screen hold the same object.
 */
export const parseScreenState = (value: unknown): ScreenState =>
  screenStateSchema.parse(typeof value === 'object' && value !== null ? value : {})

/** The unfiltered screen: "of M". */
export const NO_FILTER: ScreenFilter = { ranges: {}, sector: null }

/**
 * The request a state asks for, against the metrics mm-be serves: a range or a sort on a key it
 * no longer has is dropped here, since mm-be answers one with a 400.
 */
export const toScreenQuery = (state: ScreenState, metrics: readonly Metric[]): ScreenQuery => {
  const keys = new Set(metrics.map((metric) => metric.key))
  return {
    ranges: knownRanges(parseFilters(state.f), keys),
    sector: state.sector ?? null,
    sort: parseSort(state.sort, new Set([...keys, ...SORT_ONLY_KEYS])),
    page: state.page,
    pageSize: state.pageSize,
  }
}

/** The query's filter alone, so a count or the every-row read is keyed without its page. */
export const filterOf = ({ ranges, sector }: ScreenFilter): ScreenFilter => ({ ranges, sector })
